<!-- Active Thread Component -->
<!-- On desktop: z-resizable keeps main content in same DOM position (no scroll jump). Panel closed = [100,0], open = [main%, side%]. -->

@if(isDragActive()) {
<div @fadeOverlay class="fixed inset-0 z-[9999] flex items-center justify-center"
    style="background: rgba(255,255,255,0.85); backdrop-filter: blur(6px);" (dragover)="$event.preventDefault()">
    <div class="flex flex-col items-center gap-4 select-none pointer-events -none">
        <div class="w-20 h-20 rounded-full flex items-center justify-center bg-arena-orange-200">
            <app-icon type="cloud-upload" source="lucide" size="xl"></app-icon>
        </div>
        <div class="text-center">
            <p class="text-lg font-semibold text-gray-900 leading-snug">Drop files here to add to chat</p>
            <p class="text-sm text-gray-400 mt-1">PDF, DOCX, XLSX, images and more up to 100 MB each</p>
        </div>
    </div>
</div>
}

<div class="relative flex flex-col overflow-hidden h-full" [class.bg-gray-50]="!embeddedThreadId()"
    [class.pointer-events-none]="workspaceMismatched()" [class.select-none]="workspaceMismatched()">
    @if(workspaceMismatched()) {
    <div class="absolute inset-0 z-40 backdrop-blur-sm bg-white/60"></div>
    }
    @if(!threadLoading()) {
    @if(permissions().restricted) {
    <div class="flex flex-col items-center justify-center h-full">
        <div class="flex flex-col items-center gap-4">
            <h1 class="text-2xl font-semibold text-center">You do not have permission to view this thread</h1>
            <p class="text-gray-500 text-center">Please contact the owner of the thread for access</p>
        </div>
    </div>
    } @else if(currentWindowWidth() >= MOBILE_BREAKPOINT) {
    <z-resizable class="w-full h-full flex-1 min-h-0" [zSizes]="desktopPanelSizes()"
        (zResizeStart)="onDesktopResizeStart()" (zResizeEnd)="onDesktopResizeEnd($event)">
        <z-resizable-panel [zDefaultSize]="65" [zMin]="desktopMinPanelSizes[0]" class="min-w-0 h-full">
            <ng-container *ngTemplateOutlet="mainContent"></ng-container>
        </z-resizable-panel>
        @if(sidePanelService.isOpen()) {
        <z-resizable-handle [zWithHandle]="true" class="!w-px !min-w-px bg-gray-200/70 hover:bg-gray-300/80" />
        }
        <z-resizable-panel [zDefaultSize]="35" [zMin]="0" [zMax]="60" class="overflow-hidden h-full">
            <div class="h-full w-full p-3 bg-transparent">
                <div
                    class="relative flex flex-col h-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <ng-container *ngTemplateOutlet="sidePanelContent"></ng-container>
                </div>
            </div>
        </z-resizable-panel>
    </z-resizable>
    } @else {
    <div class="flex flex-col flex-1 min-h-0 h-full overflow-hidden">
        <ng-container *ngTemplateOutlet="mainContent"></ng-container>
    </div>
    }
    } @else {
    <app-thread-skeleton [messageCount]="4"></app-thread-skeleton>
    }
</div>

<!-- Main Content Template -->
<ng-template #mainContent>
    <div class="flex flex-col mx-auto w-full flex-1 min-w-0 min-h-0 relative bg-transparent h-full overflow-hidden">
        <!-- Thread Header -->
        <div class="absolute top-0 left-0 right-0 z-10 p-3 flex items-center gap-2 backdrop-blur-xl">
            <span
                class="text-base lg:text-lg font-semibold font-bricolage whitespace-nowrap overflow-hidden text-ellipsis flex-1 min-w-0 block">{{
                (threadTitle()) | truncate:60 }}</span>

            <!-- Credits display -->
            @if (userCreditsState.credits()) {
            @if (currentModeConsumedPct() !== null && currentModeConsumedPct()! >= 95) {
            <span class="text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap bg-arena-red-100 text-arena-red">You
                have used {{ currentModeConsumedPct() }}% of your {{ currentMode() === 'AGENTIC' ? 'Agent' : 'Ask' }}
                Credits this month</span>
            } @else if (currentModeConsumedPct() !== null && currentModeConsumedPct()! >= 80) {
            <span class="text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap bg-amber-100 text-amber-600">You
                have used {{ currentModeConsumedPct() }}% of your {{ currentMode() === 'AGENTIC' ? 'Agent' : 'Ask' }}
                Credits this month</span>
            }
            }

            <!-- Thread Actions -->
            <div class="flex items-center gap-1.5">
                @if(isOwner() && isFeatureEnabled(THREAD_AGENT_FEATURES.SHARE_THREAD)) {
                <app-share-thread class="hidden lg:inline-flex" [threadId]="threadId()"
                    [threadMetadataId]="threadMetadataId()"></app-share-thread>
                }
                @if(isFeatureEnabled(THREAD_AGENT_FEATURES.THREAD_FILES)) {
                <app-thread-files class="hidden lg:inline-flex" [threadId]="threadId()" (fileOpened)="openFile($event)"
                    pTooltip="Attachments">
                </app-thread-files>
                }

                @if(menuItems().length > 0) {
                <app-menu #menu [items]="menuItems()">
                    <app-button (clicked)="menu.toggle($event)" variant="outlined" severity="contrast" pTooltip="More"
                        tooltipPosition="bottom" icon="ellipsis-vertical" iconSource="lucide"></app-button>
                </app-menu>
                }

                @if(showHistoryButton()) {
                <app-button variant="outlined" severity="contrast" icon="history" iconSource="lucide"
                    pTooltip="Chat history" tooltipPosition="bottom"
                    (clicked)="toggleHistoryRequest.emit($event)"></app-button>
                }
                @if(isFeatureEnabled(THREAD_AGENT_FEATURES.SIDE_PANEL) && sidePanelService.isOpen()) {
                <app-button class="hidden lg:inline-flex" variant="outlined" severity="contrast"
                    [icon]="'panel-right-close'" iconSource="lucide" pTooltip="Toggle side panel"
                    (clicked)="toggleDesktopSidePanel()"></app-button>
                }
            </div>
        </div>

        <!-- Messages Area -->
        <div #scrollContainer class="flex-1 overflow-hidden w-full relative min-h-0">
            <app-message-list [isOwner]="isOwner()" [canEdit]="permissions().canEdit"
                [isEmbedded]="!!embeddedThreadId()" [threadMetadataId]="threadMetadataId()"
                [isSummarizing]="isSummarizing()" [summarizingTaskIds]="summarizingTaskIds()"
                (scroll)="onScroll($event)" (editMessage)="handleMessageEdit($event)" [startTimer]="true"
                [pauseTimer]="showSaveAsPromptDialog()" (approvePlan)="onApprovePlan()" (rejectPlan)="onRejectPlan()"
                (editPlan)="onEditPlan($event)" (revisePlan)="onRevisePlan($event)"
                (timerExpired)="onPlanTimerExpired()" (continueAfterError)="handleContinueAfterStreamingError()"
                (continueAfterPause)="handleContinueAfterPause()" (feedback)="submitFeedback($event)"
                (saveAsPrompt)="openSaveAsPromptDialog($event)" (saveEnhancement)="openSaveEnhancementDialog($event)"
                (openImagePreview)="openImagePreview($event)" (openCanvasPanel)="openCanvasPanel($event)"
                (downloadCanvas)="downloadCanvas($event)" (useExistingPrompt)="onUseExistingPrompt($event)"
                (generateNewPrompt)="onGenerateNewPrompt()" class="w-full block h-full pt-[3.5rem]">
            </app-message-list>
        </div>

        <!-- Thread Input -->
        @if(permissions().canEdit) {
        <div class="flex-shrink-0 z-50 w-full backdrop-blur pb-3 lg:pb-4 pt-2"
            [ngClass]="{'bg-gray-50/90': !embeddedThreadId()}">
            <div class="max-w-4xl mx-auto px-4">
                <div [class.pointer-events-none]="!isOwner()" [class.opacity-50]="!isOwner()">
                    <app-thread-input [provider]="selectedProvider()" [threadId]="threadId()"
                        [threadMetadataId]="threadMetadataId()"
                        [threadContextReady]="!!(threadId() && threadMetadataId())" [needTokenLimits]="true"
                        [hasThreadSizeError]="false" [showEmptyState]="false" [documentId]="documentId()"
                        [documentTypeId]="documentTypeId()" [projectId]="projectId()"
                        [compareDocuments]="compareDocuments()" [kpiDocuments]="kpiDocuments()"
                        [formQuestions]="formQuestions()" [selectedFormQuestionId]="selectedFormQuestionId()"
                        [restoredCustomInstructions]="restoredCustomInstructions()"
                        [restoredInputState]="restoredInputState()" [embeddingsStatus]="embeddingsStatus()"
                        (sendMessageEvent)="sendMessage($event)" (pauseThreadEvent)="pauseThread()"
                        (modeChange)="currentMode.set($event)" (attachmentsChange)="onAttachmentsChange($event)"
                        [renderDragOverlay]="false" (windowDragActiveChange)="isDragActive.set($event)">
                    </app-thread-input>
                </div>
            </div>
        </div>
        }
    </div>
</ng-template>

<!-- Desktop Side Panel Content Template -->
<ng-template #sidePanelContent>
    @if(sidePanelService.activeView() === 'TOOL_DETAILS') {
    <div class="h-full w-full flex flex-col">
        <ng-container *ngTemplateOutlet="bobsComputerHeader; context: { showClose: true }"></ng-container>
        <div class="flex-1 overflow-hidden">
            <app-tool-panel [toolCall]="currentToolCall()" [isLoading]="isToolLoading()"></app-tool-panel>
        </div>
        <ng-container *ngTemplateOutlet="navigationFooter; context: { mobile: false }"></ng-container>
    </div>
    } @else if(sidePanelService.activeView() === 'SOURCES') {
    <div class="h-full w-full flex flex-col">
        <app-sources-panel [isResizing]="desktopIsResizing()"></app-sources-panel>
    </div>
    } @else if(sidePanelService.activeView() === 'FILE_PREVIEW') {
    <div class="h-full w-full flex flex-col">
        <app-file-preview class="h-full w-full" (closePreview)="closeDocumentPreview()"></app-file-preview>
    </div>
    } @else if(sidePanelService.activeView() === 'CANVAS') {
    <div class="h-full w-full flex flex-col pt-[1px]">
        <ng-container *ngTemplateOutlet="canvasPanelHeader"></ng-container>
        <ng-container *ngTemplateOutlet="canvasPanelContent"></ng-container>
    </div>
    } @else {
    <div class="h-full w-full flex items-center justify-center text-zinc-400">
        <div class="text-center">
            <p>Select an item to view details</p>
        </div>
    </div>
    }
</ng-template>

<ng-template #canvasPanelHeader>
    <div class="p-4 text-zinc-900 bg-zinc-50 border-b border-zinc-200 flex-shrink-0 w-full rounded-tr-2xl">
        <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5 min-w-0 flex-1">
                <app-icon [type]="'file-text'" [class]="'h-4 w-4 flex-shrink-0'" [source]="'lucide'"
                    [size]="'default'"></app-icon>
                <h2 class="text-md font-medium truncate max-w-[300px] lg:max-w-[200px]">{{
                    canvasDetails()!.message['additional_kwargs']?.canvasName || 'Canvas'}}</h2>
            </div>
            <div class="flex items-center gap-3 flex-shrink-0">
                <div class="cursor-pointer" (click)="copyCanvas()" pTooltip="Copy" tooltipPosition="bottom">
                    <app-icon [type]="copyCanvasIcon" [class]="'h-4 w-4 text-zinc-900'" [source]="'lucide'"
                        [size]="'default'"></app-icon>
                </div>
                <div class="cursor-pointer" (click)="downloadCanvas(canvasDetails()!.message)" pTooltip="Download"
                    tooltipPosition="bottom">
                    <app-icon [type]="'square-arrow-down'" [class]="'h-4 w-4 text-zinc-900'" [source]="'lucide'"
                        [size]="'default'"></app-icon>
                </div>
                <div class="cursor-pointer" (click)="sidePanelService.close()">
                    <app-icon [type]="'x'" [class]="'h-4 w-4 text-zinc-900'" [source]="'lucide'"
                        [size]="'default'"></app-icon>
                </div>
            </div>
        </div>
    </div>
</ng-template>

<ng-template #canvasPanelContent>
    <div class="p-4 overflow-hidden flex-1 h-full">
        <app-canvas [message]="canvasDetails()!.message" [isEditable]="canvasDetails()!.isEditable"
            [threadMetadataId]="threadMetadataId()" (editCanvasWithAi)="editCanvasWithAi($event)"></app-canvas>
    </div>
</ng-template>


<!-- Reusable Header Templates -->
<ng-template #bobsComputerHeader let-showClose="showClose">
    <div
        [ngClass]="showClose ? 'p-4 text-zinc-900 bg-zinc-50 border-b border-zinc-200 flex-shrink-0' : 'flex items-center gap-1.5'">
        <div class="flex items-center" [ngClass]="showClose ? 'justify-between' : 'gap-1.5'">
            <div class="flex items-center gap-1.5">
                <app-icon [type]="'computer'" [class]="'h-4 w-4'" [source]="'lucide'" [size]="'default'"></app-icon>
                <h2 class="text-md font-medium">Bob's Computer</h2>
            </div>
            @if(showClose) {
            <div class="cursor-pointer" (click)="toggleToolCallSidePanel()">
                <app-icon [type]="'x'" [class]="'h-4 w-4 text-zinc-900'" [source]="'lucide'" [size]="'sm'"></app-icon>
            </div>
            }
        </div>
    </div>
</ng-template>



<ng-template #navigationFooter let-mobile="mobile">
    @if (totalToolCalls() > 1) {
    <div
        [ngClass]="mobile ? 'flex flex-col border-t border-zinc-200 bg-zinc-50 px-4 py-3 flex-shrink-0' : 'flex flex-col border-t border-zinc-200 bg-zinc-50 px-4 py-4 flex-shrink-0'">
        <div
            [ngClass]="mobile ? 'flex justify-between items-center gap-1.5 mb-2' : 'flex justify-between items-center gap-1.5'">
            <div class="flex items-center gap-1.5 min-w-0">
                <div class="h-5 w-5 rounded-full bg-zinc-100 flex items-center justify-center">
                    <app-icon [type]="toolIcon().icon" [class]="'h-4 w-4'" [source]="toolIcon().iconSource || 'lucide'"
                        [size]="'sm'"></app-icon>
                </div>
                <span class="text-xs font-medium text-zinc-700 truncate" [attr.title]="currentToolName()">
                    {{ currentToolName() }}
                </span>
            </div>
            <div class="flex items-center gap-1.5 flex-shrink-0">
                <span class="text-xs text-zinc-500 whitespace-nowrap">
                    Step {{ currentToolCallIndex() + 1 }} of {{ totalToolCalls() }}
                </span>
            </div>
        </div>
        <div [ngClass]="mobile ? 'flex items-center gap-1.5' : 'flex items-center gap-3 py-1'">
            @if(!mobile) {
            <div class="flex items-center gap-1.5">
                <app-button variant="text" [size]="'sm'" [disabled]="currentToolCallIndex() <= 0" styleClass="!px-0"
                    (clicked)="navigateToPreviousToolCall()" [iconSource]="'lucide'" [iconSize]="'default'"
                    [icon]="'chevron-left'" />
                <app-button variant="text" [size]="'sm'" [disabled]="currentToolCallIndex() >= totalToolCalls() - 1"
                    styleClass="!px-0" (clicked)="navigateToNextToolCall()" [iconSource]="'lucide'"
                    [iconSize]="'default'" [icon]="'chevron-right'" />
            </div>
            }
            @else {
            <app-button variant="text" [size]="'sm'" [disabled]="currentToolCallIndex() <= 0" styleClass="!px-2"
                (clicked)="navigateToPreviousToolCall()" [iconSource]="'lucide'" [iconSize]="'default'"
                [icon]="'chevron-left'" />
            }

            <div [ngClass]="mobile ? 'relative flex-1 min-w-0' : 'relative w-full min-w-0'">
                <p-slider [min]="0" [max]="totalToolCalls() - 1" [ngModel]="currentToolCallIndex()"
                    (ngModelChange)="onToolCallSliderChange($event)" styleClass="w-full"></p-slider>
            </div>

            @if(mobile) {
            <app-button variant="text" [size]="'sm'" [disabled]="currentToolCallIndex() >= totalToolCalls() - 1"
                styleClass="!px-2" (clicked)="navigateToNextToolCall()" [iconSource]="'lucide'" [iconSize]="'default'"
                [icon]="'chevron-right'" />
            }
        </div>
    </div>
    }
</ng-template>


<!-- Dialogs -->
<app-edit-thread [(visible)]="editThreadDialogVisible" [(threadTitle)]="threadTitleToEdit"
    (onSave)="updateThreadTitle()">
</app-edit-thread>

<app-image-preview [imageUrl]="previewImageUrl()" (onClose)="previewImageUrl.set('')"></app-image-preview>

@if(deleteThreadDialogVisible()){
<app-delete-confirmation [(visible)]="deleteThreadDialogVisible" headerText="Are you sure?"
    bodyText="This action will delete the thread" confirmButtonText="Proceed" (onConfirm)="deleteThread()">
</app-delete-confirmation>
}

<!-- Mobile Dialogs -->
@if(currentWindowWidth() < MOBILE_BREAKPOINT) { <app-dialog
    [visible]="sidePanelService.isOpen() && sidePanelService.isToolDetailsView()" [fullscreenOnMobile]="true"
    [showCloseButton]="false" class="!p-0 !gap-0 !max-w-[75dvw] !h-[90dvh]" (closed)="sidePanelService.close()">
    <ng-template #headerTemplate>
        <ng-container *ngTemplateOutlet="bobsComputerHeader; context: { showClose: true }"></ng-container>
    </ng-template>
    <ng-template #bodyTemplate>
        <app-tool-panel [toolCall]="currentToolCall()" [isLoading]="isToolLoading()"></app-tool-panel>
    </ng-template>
    <ng-template #footerTemplate>
        <ng-container *ngTemplateOutlet="navigationFooter; context: { mobile: true }" class="flex-col"></ng-container>
    </ng-template>
    </app-dialog>
    }

    <!-- Sources Panel Dialog -->
    @if(isSourcesPanelOpen() && currentWindowWidth() < MOBILE_BREAKPOINT) { <app-dialog [visible]="true"
        [fullscreenOnMobile]="true" [showCloseButton]="true" class="!p-0 !gap-0 w-75dvw !h-[90dvh]"
        (closed)="toggleSourcesPanel()">
        <ng-template #bodyTemplate>
            <app-sources-panel></app-sources-panel>
        </ng-template>
        </app-dialog>
        }

        <!-- Document Preview Dialog -->
        @if(isDocumentPreviewPanelOpen() && (currentWindowWidth() < MOBILE_BREAKPOINT || isDocumentFullscreenOpen())) {
            <app-dialog [visible]="true" [fullscreenOnMobile]="true" [showCloseButton]="false"
            class="!p-0 !gap-0 !max-w-[75dvw] !h-[90dvh]"
            (closed)="isDocumentFullscreenOpen() ? closeDocumentFullscreen() : closeDocumentPreview()">
            <ng-template #bodyTemplate>
                <app-file-preview class="h-full w-full" (closePreview)="closeDocumentPreview()"></app-file-preview>
            </ng-template>
            </app-dialog>
            }

            <!-- Canvas Panel Dialog -->
            @if(isCanvasPanelOpen() && currentWindowWidth() < MOBILE_BREAKPOINT) { <app-dialog [visible]="true"
                [fullscreenOnMobile]="true" [showCloseButton]="false" class="!p-0 !gap-0 !max-w-[75dvw] !h-[90dvh]"
                (closed)="sidePanelService.close()">
                <ng-template #headerTemplate>
                    <ng-container *ngTemplateOutlet="canvasPanelHeader"></ng-container>
                </ng-template>
                <ng-template #bodyTemplate>
                    <ng-container *ngTemplateOutlet="canvasPanelContent"></ng-container>
                </ng-template>
                </app-dialog>
                }

                <!-- No Credits Dialog -->
                <app-dialog [(visible)]="noCreditsDialogVisible" title="No Credits Left" [showCloseButton]="false"
                    [closable]="false" class="max-w-[30rem]">
                    <ng-template #bodyTemplate>
                        <div>
                            <p class="mb-0">You have used 100% of your {{ currentMode() === 'AGENTIC' ? 'Agent' : 'Ask'
                                }} Credits
                                this month. Please contact your account owner to request more credits.</p>
                        </div>
                    </ng-template>
                    <ng-template #footerTemplate>
                        <div class="flex justify-end mt-4">
                            <app-button label="Dismiss" (clicked)="noCreditsDialogVisible.set(false)"
                                variant="outlined" />
                        </div>
                    </ng-template>
                </app-dialog>
                <!-- Save-as-prompt dialog: opens when user clicks the bookmark icon on an AI message -->
                <app-prompt-editor-dialog [visible]="showSaveAsPromptDialog()" dialogTitle="Save as Prompt"
                    saveLabel="Save to Library" [initialTitle]="saveAsPromptForm().title"
                    [initialContent]="saveAsPromptForm().content" [groupOptions]="saveAsPromptGroupOptions()"
                    [loading]="isSavingPrompt()" (saved)="onSaveAsPromptEditorSaved($event)"
                    (closed)="closeSaveAsPromptDialog()" />

                <!-- Workspace Mismatch Dialog -->
                <app-dialog [visible]="workspaceMismatched()" title="You're in the Wrong Workspace"
                    [showCloseButton]="false" [closable]="false" class="max-w-[30rem]">
                    <ng-template #bodyTemplate>
                        <div class="px-6 py-4">
                            <p class="mb-0">This thread is available in a different workspace. Please switch to the
                                appropriate
                                workspace to access it.</p>
                        </div>
                    </ng-template>
                    <ng-template #footerTemplate>
                        <div class="flex justify-end">
                            <app-button label="Switch Workspace" (clicked)="switchToThreadWorkspace()"
                                severity="primary" variant="filled" icon="log-out" [iconSource]="'lucide'" />
                        </div>
                    </ng-template>
                </app-dialog>